import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Error400, Gray300, Primary500 } from '@mehilainen/design-system-tokens/colors'
import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from '@mui/material'
import { TFunctionResult } from 'i18next'
import React from 'react'

import { scale } from '../../utils/scale'
import { ColumnFlex } from '../Layout/Layout'
import { Text } from '../Typography/Typography'

const StyledFormControlLabel = styled(FormControlLabel)<{ checked?: boolean; error: boolean }>`
  display: flex;
  margin: 0;
  margin-bottom: ${scale(0.5)};
  padding: ${scale(2)};
  outline: 1px solid ${Gray300};
  border-radius: 6px;

  ${(props) =>
    props.checked &&
    css`
      outline: 2px solid ${Primary500};
    `}

  :hover, :focus-within {
    outline: 2px solid ${Primary500};
  }

  .MuiButtonBase-root {
    padding: 0;
    margin-right: ${scale(2)};
    align-self: baseline;
  }

  ${(props) =>
    props.error &&
    css`
      outline: 2px solid ${Error400} !important;
    `}
`

interface Props extends Omit<CheckboxProps, 'disableRipple'> {
  label: string
  description: string
  error: boolean
  helperText?: string | null | false | TFunctionResult
}

const FormCheckbox: React.FC<Props> = ({
  label,
  description,
  helperText,
  error,
  color = 'primary',
  id,
  required,
  ...checkboxProps
}) => {
  const helperTextId = `${id}-helperText`

  return (
    <FormControl>
      <StyledFormControlLabel
        error={error}
        checked={checkboxProps.checked}
        control={
          <Checkbox
            id={id}
            aria-describedby={helperText ? helperTextId : undefined}
            required={required}
            disableRipple
            color={color}
            {...checkboxProps}
          />
        }
        label={
          <ColumnFlex>
            <Text $size={400} $weight="Medium" $height="Medium">
              {label}
              {required ? ' *' : ''}
            </Text>
            <Text $size={200} $height="Large">
              {description}
            </Text>
          </ColumnFlex>
        }
      />
      {helperText && (
        <FormHelperText id={helperTextId} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default FormCheckbox
